<template>
  <div class="flex min-h-0 w-full flex-1 flex-col gap-2">
    <div class="select-project-compact relative">
      <FontAwesomeIcon
        :icon="['far', 'magnifying-glass']"
        class="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-gray-400"
        size="xs"
      />
      <SelectProject
        :is_dropdown="false"
        :label="$t('project.labels.searchProject')"
        @changeSelect="(q) => (search_project = q ? q.attributes.name : '')"
        @onSearch="(q) => (search_project = q)"
      />
    </div>

    <div class="scrollbar-quiet -mr-4 flex-1 overflow-auto pr-2">
      <ProjectSection
        v-for="section in project_sections"
        :key="section.title"
        v-bind="section.props"
        v-on="section.events"
      />
    </div>
  </div>

  <PrimeModal
    v-if="limit_reached_modal"
    :has_parent="false"
    :is_small="false"
    :is_medium="true"
    :hide_maximize_button="true"
    @action-cancel="limit_reached_modal = false"
    @action-primary="
      () => {
        window.Intercom('show');
        limit_reached_modal = false;
      }
    "
  >
    <template #title>{{ $t('project.labels.limitReached') }}</template>
    <template #content>
      <div
        class="mx-auto flex max-w-3xl flex-col items-center gap-6 rounded-lg border-2 border-primary-500 bg-orange-50 p-8 py-4"
      >
        <div class="flex flex-col items-center justify-center gap-4 sm:flex-row sm:items-center">
          <div class="shrink-0">
            <img
              class="h-32 w-32 object-contain sm:h-40 sm:w-40"
              :src="Ziggy_laptop"
              alt="Ziggy with laptop"
            />
          </div>
          <div class="flex flex-1 flex-col justify-center gap-2 text-center sm:text-left">
            <p class="font-medium text-surface-700">
              {{ $t('project.labels.outOfProjects') }}
            </p>
            <p class="text-sm text-surface-600">
              {{ $t('project.labels.outOfProjectsContact') }}
            </p>
          </div>
        </div>
      </div>
    </template>
    <template #ButtonPrimary>{{ $t('modal.actions.contact_ziggu') }}</template>
  </PrimeModal>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, provide, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import SelectProject from '@/app/shared_components/selects/SelectProject.vue';
import ProjectSection from '@/app/_employees/components/sidebars/ProjectSection.vue';
import PrimeModal from '@/app/shared_components/modals/ThePrimeModal.vue';
import TheProjectModal from '@/app/_employees/components/projects/form/TheProjectModal.vue';
import Ziggy_laptop from '@/assets/images/ziggy/Ziggy_laptop.png';
import { Assignment, Employee, LEVEL_TYPE, Project, PROJECT_TYPES } from '@/app/data/model_constants';
import { useToasts } from '@/app/util/toasts';

const props = defineProps({
  on_close_mobile_drawer: { type: Function, default: () => {} },
});

const { proxy: app } = getCurrentInstance();
const { t } = useI18n();
const { toast_action_failed } = useToasts();
const store = useStore();

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);
const projects_all = computed(() => store.getters.projects);

const active_popover_project_id = ref(null);
provide('active_popover_project_id', active_popover_project_id);

const assignments = ref([]);
const project_ids_follow_pending = ref({});
const loaded_other_projects = ref(false);
const loaded_project_templates = ref(false);
const search_project = ref('');
const toggle_projects_follow = ref(true);
const toggle_projects_other = ref(false);
const toggle_projects_template = ref(false);
const toggle_projects_deactivated = ref(false);
const is_fetching_other = ref(false);
const is_fetching_template = ref(false);
const is_fetching_deactivated = ref(false);
const limit_reached_modal = ref(false);

function filterProjects(projects, filterFn) {
  const search = search_project.value.toLowerCase();
  return (Array.isArray(projects) ? projects : Object.values(projects))
    .filter((project) => {
      if (!project) return false;
      if (!filterFn(project)) return false;
      if (search) return project.attributes.name.toLowerCase().includes(search);
      return true;
    })
    .sort((a, b) => (a.attributes.name || '').localeCompare(b.attributes.name || ''));
}

const projects_follow = computed(() =>
  filterProjects(
    _.pick(projects_all.value, _.map(assignments.value, 'attributes.assigned_id')),
    (project) =>
      project.attributes.status === Project.STATUS_ENABLED &&
      !project.attributes.skip_customer &&
      project.attributes.activated,
  ),
);

const projects_other = computed(() =>
  filterProjects(
    _.omit(projects_all.value, _.map(assignments.value, 'attributes.assigned_id')),
    (project) =>
      project.attributes.status === Project.STATUS_ENABLED &&
      !project.attributes.skip_customer &&
      project.attributes.activated,
  ),
);

const projects_template = computed(() =>
  filterProjects(projects_all.value, (project) => project.attributes.skip_customer),
);

const projects_deactivated = computed(() =>
  filterProjects(projects_all.value, (project) => !project.attributes.skip_customer && !project.attributes.activated),
);

const is_owner = computed(() => current_profile.value.attributes.role === Employee.ROLE_OWNER);

const remaining_unit_count = computed(
  () => current_client.value.attributes.units_active_count_max - current_client.value.attributes.units_active_count,
);

watch(search_project, () => {
  if (!search_project.value) return;
  toggle_projects_other.value = _.size(projects_other.value) > 0;
  toggle_projects_follow.value = _.size(projects_follow.value) > 0;
  toggle_projects_template.value = _.size(projects_template.value) > 0;
  toggle_projects_deactivated.value = _.size(projects_deactivated.value) > 0;
});

onMounted(async () => {
  const [assignments_fetched] = await Promise.all([
    store.dispatch('FETCH_ASSIGNMENTS', {
      assigned_type: LEVEL_TYPE.PROJECT,
      assignee_type: current_profile.value.type,
      assignee_id: current_profile.value.id,
      assignment_type: Assignment.TYPE_FOLLOW,
    }),
    store.dispatch('FETCH_PROJECTS', { followed: true }),
  ]);
  assignments.value = assignments_fetched;
});

async function createAssignment(assignment) {
  const project_id = String(assignment.attributes.assigned_id);
  const assignment_existing = assignments.value.find(
    (assignment_item) =>
      assignment_item.attributes.assigned_id === project_id &&
      assignment_item.attributes.assignee_id === String(current_profile.value.id) &&
      assignment_item.attributes.assignee_type === current_profile.value.type &&
      assignment_item.attributes.assignment_type === Assignment.TYPE_FOLLOW,
  );
  if (assignment_existing || project_ids_follow_pending.value[project_id]) return;

  project_ids_follow_pending.value = {
    ...project_ids_follow_pending.value,
    [project_id]: true,
  };

  try {
    const assignment_new = await store.dispatch('CREATE_ASSIGNMENT', { record: assignment });
    const assignment_added = assignments.value.some((assignment_item) => assignment_item.id === assignment_new.id);
    if (!assignment_added) assignments.value.push(assignment_new);
  } catch (error) {
    toast_action_failed({ error });
  } finally {
    const { [project_id]: _ignored, ...remaining } = project_ids_follow_pending.value;
    project_ids_follow_pending.value = remaining;
  }
}

async function deleteAssignment(assignment) {
  await store.dispatch('DELETE_ASSIGNMENT', { record: assignment });
  assignments.value = assignments.value.filter((a) => a.id !== assignment.id);
}

async function goToProjectCreate() {
  if (app.$flipper.enabled(app.$FT.TEMPLATE_PROJECTS) && !loaded_project_templates.value) {
    await store.dispatch('FETCH_PROJECTS', { skip_customer: true });
    loaded_project_templates.value = true;
  }
  props.on_close_mobile_drawer();
  if (current_client.value.attributes.project_type === PROJECT_TYPES.SUP && remaining_unit_count.value <= 0) {
    limit_reached_modal.value = true;
  } else {
    app.$buefy.modal.open({ component: TheProjectModal });
  }
}

async function toggleOtherProjects() {
  is_fetching_other.value = true;
  toggle_projects_other.value = !toggle_projects_other.value;
  if (!loaded_other_projects.value) {
    await store.dispatch('FETCH_PROJECTS', { skip_customer: false, followed: false });
    loaded_other_projects.value = true;
  }
  is_fetching_other.value = false;
}

async function toggleProjectTemplates() {
  is_fetching_template.value = true;
  toggle_projects_template.value = !toggle_projects_template.value;
  if (!loaded_project_templates.value) {
    await store.dispatch('FETCH_PROJECTS', { skip_customer: true });
    loaded_project_templates.value = true;
  }
  is_fetching_template.value = false;
}

async function toggleDeactivatedProjects() {
  is_fetching_deactivated.value = true;
  toggle_projects_deactivated.value = !toggle_projects_deactivated.value;
  // shares the "other" fetch — deactivated non-followed projects come in via {skip_customer: false, followed: false}
  if (!loaded_other_projects.value) {
    await store.dispatch('FETCH_PROJECTS', { skip_customer: false, followed: false });
    loaded_other_projects.value = true;
  }
  is_fetching_deactivated.value = false;
}

const project_sections = computed(() => [
  {
    title: t('project.labels.followedProjects'),
    props: {
      title: t('project.labels.followedProjects'),
      is_open: toggle_projects_follow.value,
      on_toggle: () => {
        toggle_projects_follow.value = !toggle_projects_follow.value;
      },
      on_create: is_owner.value ? goToProjectCreate : null,
      projects: projects_follow.value,
      item_props: {
        assignments: assignments.value,
      },
    },
    events: {
      closeProjectSidebar: props.on_close_mobile_drawer,
      unfollow: (assignment) => deleteAssignment(assignment),
    },
  },
  {
    title: t('project.labels.otherProjects'),
    props: {
      title: t('project.labels.otherProjects'),
      is_open: toggle_projects_other.value,
      is_fetching: is_fetching_other.value,
      on_toggle: toggleOtherProjects,
      projects: projects_other.value,
      item_props: {
        assignments: assignments.value,
        project_ids_follow_pending: project_ids_follow_pending.value,
      },
    },
    events: {
      closeProjectSidebar: props.on_close_mobile_drawer,
      follow: (assignment) => createAssignment(assignment),
    },
  },
  {
    title: t('project.labels.templateProjects'),
    props: {
      title: t('project.labels.templateProjects'),
      is_open: toggle_projects_template.value,
      is_fetching: is_fetching_template.value,
      on_toggle: toggleProjectTemplates,
      projects: projects_template.value,
      item_props: {
        assignments: assignments.value,
      },
    },
    events: {
      closeProjectSidebar: props.on_close_mobile_drawer,
    },
  },
  {
    title: t('project.labels.deactivatedProjects'),
    props: {
      title: t('project.labels.deactivatedProjects'),
      is_open: toggle_projects_deactivated.value,
      is_fetching: is_fetching_deactivated.value,
      on_toggle: toggleDeactivatedProjects,
      projects: projects_deactivated.value,
      item_props: {
        assignments: assignments.value,
      },
    },
    events: {
      closeProjectSidebar: props.on_close_mobile_drawer,
    },
  },
]);
</script>

<style scoped>
.select-project-compact :deep(input) {
  padding: 0.25rem 0.5rem 0.25rem 1.75rem;
  font-size: 0.75rem;
  line-height: 1.5rem;
  min-height: 0;
  background: var(--color-sunken);
  border: none;
  box-shadow: none;
}
.select-project-compact :deep(input:focus) {
  background: var(--color-neutral-100);
  box-shadow: none;
  outline: none;
}
.select-project-compact :deep(input::placeholder) {
  color: var(--color-neutral-500);
  opacity: 1;
}
</style>
